import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import classes from "./newMenu.module.scss";
import { PagesPermissions } from "../types/Authorization";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { isPermissionGranted } from "../utils/navPermissions";
import useCustomNavigate from "../hooks/useCustomNavigate";
import TaskFormModal from "../components/Task/TaskFormModal";

interface CreateEntry {
    key: string;
    label: string;
    hint: string;
    icon: string;
    permission: string;
    section: string;
    /** Route of the create page; entries without one open a modal. */
    to?: string;
}

const CREATE_ENTRIES: CreateEntry[] = [
    { key: "task", label: "Task", hint: "Assign work with a due date", icon: "bi-check2-square", permission: PagesPermissions.TASKS_CREATE, section: "Work" },
    { key: "mission-request", label: "Mission request", hint: "Scope and price a client's request", icon: "bi-file-earmark-text", permission: PagesPermissions.MISSION_REQUESTS_CREATE, section: "Work", to: "/mission-request" },
    { key: "mission", label: "Mission", hint: "Plan a new mission", icon: "bi-bullseye", permission: PagesPermissions.MISSIONS_CREATE, section: "Work", to: "/mission" },
    { key: "lead", label: "Lead", hint: "Capture a new vendor or client", icon: "bi-person-plus", permission: PagesPermissions.LEADS_CREATE, section: "People & companies", to: "/lead" },
    { key: "client", label: "Client", hint: "A company you deliver for", icon: "bi-building", permission: PagesPermissions.CLIENTS_CREATE, section: "People & companies", to: "/client" },
    { key: "provider", label: "Provider", hint: "A company that delivers services", icon: "bi-briefcase", permission: PagesPermissions.PROVIDERS_CREATE, section: "People & companies", to: "/provider" },
    { key: "contact", label: "Contact", hint: "A person at a client or provider", icon: "bi-person-vcard", permission: PagesPermissions.CONTACTS_CREATE, section: "People & companies", to: "/contact" },
    { key: "invoice", label: "Invoice", hint: "Bill a client", icon: "bi-receipt", permission: PagesPermissions.INVOICES_CREATE, section: "Finance", to: "/finance/invoice/new" },
    { key: "bill", label: "Bill", hint: "Record what a provider charges", icon: "bi-file-earmark-ruled", permission: PagesPermissions.BILLS_CREATE, section: "Finance", to: "/finance/bill/new" },
    { key: "payment", label: "Payment", hint: "Money received or paid out", icon: "bi-cash-coin", permission: PagesPermissions.PAYMENTS_CREATE, section: "Finance", to: "/finance/payment/new" },
];

/**
 * Header "+ New" menu — create anything from any page. Lists only what the
 * user's role can create; renders nothing when that is nothing.
 */
const NewMenu: React.FC = () => {
    const { currentUser } = useUserPermissions();
    const navigate = useCustomNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const sections = useMemo(() => {
        const pages = currentUser?.pages || [];
        const allowed = CREATE_ENTRIES.filter((entry) => isPermissionGranted(pages, entry.permission));
        const grouped = new Map<string, CreateEntry[]>();
        allowed.forEach((entry) => grouped.set(entry.section, [...(grouped.get(entry.section) || []), entry]));
        return Array.from(grouped.entries());
    }, [currentUser]);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("mousedown", handleOutside);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    if (sections.length === 0) return null;

    const handlePick = (entry: CreateEntry) => {
        setIsOpen(false);
        if (entry.to) {
            navigate(entry.to);
        } else {
            setShowTaskModal(true);
        }
    };

    return (
        <div className={classes.wrapper} ref={wrapperRef}>
            <button
                type="button"
                className={`btn-unstyled ${classes.trigger}`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((prev) => !prev)}
                title="Create a task, mission, contact…"
            >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                <span className={classes.triggerLabel}>New</span>
                <i className="bi bi-chevron-down" aria-hidden="true" />
            </button>
            {isOpen && (
                <div className={classes.menu} role="menu" aria-label="Create">
                    {sections.map(([section, entries]) => (
                        <div key={section} className={classes.section}>
                            <span className={classes.sectionTitle}>{section}</span>
                            {entries.map((entry) => (
                                <button
                                    key={entry.key}
                                    type="button"
                                    role="menuitem"
                                    className={`btn-unstyled ${classes.item}`}
                                    onClick={() => handlePick(entry)}
                                >
                                    <i className={`bi ${entry.icon} ${classes.itemIcon}`} aria-hidden="true" />
                                    <span className={classes.itemText}>
                                        <span className={classes.itemLabel}>{entry.label}</span>
                                        <span className={classes.itemHint}>{entry.hint}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
            <TaskFormModal show={showTaskModal} onClose={() => setShowTaskModal(false)} />
        </div>
    );
};

export default NewMenu;
